import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FriendshipService } from 'src/app/services/relations/relations.service';
import { ChatService } from 'src/app/services/chat/chat.service';
import { FormServiceService } from 'src/app/services/publications/form-service.service';
import { UserContact } from 'src/app/Models/Classes/User/user-contact';
import { Message } from 'src/app/Models/Classes/Chat/message';
import * as QRCode from 'qrcode';

@Component({
  selector: 'app-share-profile-modal',
  templateUrl: './share-profile-modal.component.html',
  styleUrls: ['./share-profile-modal.component.scss'],
})
export class ShareProfileModalComponent implements OnInit {
  @Input() sharedProfile: any;
  @Input() sharedProfileId: number;

  friends: any[] = [];
  filteredFriends: any[] = [];
  search = '';
  loading = true;
  sendingProfileId?: number;
  profileShareUrl = '';
  profileQrCodeUrl = '';

  constructor(
    private modalController: ModalController,
    private friendshipService: FriendshipService,
    private chatService: ChatService,
    public formService: FormServiceService,
  ) {}

  ngOnInit(): void {
    this.profileShareUrl = this.buildProfileShareUrl();
    void this.generateProfileQrCode();
    this.loadFriends();
  }

  close(): void {
    this.modalController.dismiss();
  }

  profilePhotoUrl(): string {
    const candidate = this.sharedProfile?.profile_photo?.url
      || this.sharedProfile?.profile_photo?.route
      || this.sharedProfile?.photo
      || '';
    if (!candidate) return 'assets/icon/usuario.png';
    if (/^(https?:|data:|blob:|capacitor:)/i.test(candidate)) return candidate;
    return `https://laniakea-multimedia.s3.us-east-1.amazonaws.com/${String(candidate).replace(/^\/+/, '')}`;
  }

  useDefaultAvatar(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image) image.src = 'assets/icon/usuario.png';
  }

  filterFriends(): void {
    const query = this.search.trim().toLowerCase();
    this.filteredFriends = !query
      ? [...this.friends]
      : this.friends.filter((friend) =>
          `${friend?.username || ''} ${friend?.full_name || friend?.name || ''}`
            .toLowerCase()
            .includes(query)
        );
  }

  async sendToFriend(friend: any): Promise<void> {
    const friendProfileId = Number(friend?.profile_id || friend?.id || 0);
    if (!friendProfileId || this.sendingProfileId) return;

    this.sendingProfileId = friendProfileId;
    try {
      const contact = new UserContact(friend);
      const conversation = await this.chatService.newConversation(contact);
      const message = new Message();
      const username = this.sharedProfile?.username || 'Laniakea';
      message.setText(
        `👤 @${username}\nhttps://supercumulo.laniakea.tv/home/profile/${this.sharedProfileId}`
      );
      await this.chatService.sendMessage(conversation, message);
      await this.modalController.dismiss({ sent: true, friend });
    } catch (error) {
      console.error('Unable to share profile with friend.', error);
    } finally {
      this.sendingProfileId = undefined;
    }
  }

  private loadFriends(): void {
    const signedInProfileId = Number(this.formService?.profile?.id || 0);
    this.friendshipService.getAllRelations({
      relationDirection: '3',
      relationType: [1],
      relationStatus: '2',
      page: '1',
      profile_id: String(signedInProfileId),
    }).then((raw: any) => {
      let data = raw;
      if (typeof data === 'string') {
        try { data = JSON.parse(data); } catch { data = []; }
      }
      const relations = Array.isArray(data) ? data : (data?.data || []);
      this.friends = relations
        .map((relation: any) =>
          Array.isArray(relation) ? relation[0]?.user_info : relation?.user_info
        )
        .filter(Boolean);
      this.filteredFriends = [...this.friends];
    }).catch((error) => {
      console.error('Unable to load friends for profile sharing.', error);
      this.friends = [];
      this.filteredFriends = [];
    }).finally(() => {
      this.loading = false;
    });
  }

  private buildProfileShareUrl(): string {
    const profileId = Number(this.sharedProfileId || this.sharedProfile?.id || this.sharedProfile?.profile_id || 0);
    const username = encodeURIComponent(String(this.sharedProfile?.username || '').trim());
    return profileId
      ? `https://supercumulo.laniakea.tv/profile/${profileId}${username ? `/${username}` : ''}`
      : 'https://supercumulo.laniakea.tv/home/profile';
  }

  private async generateProfileQrCode(): Promise<void> {
    try {
      this.profileQrCodeUrl = await QRCode.toDataURL(this.profileShareUrl, {
        margin: 1,
        width: 260,
        errorCorrectionLevel: 'M',
      });
    } catch (error) {
      console.error('Unable to generate profile QR code.', error);
    }
  }
}
